<template>
  <div
    ref="container"
    class="media-content critical-component-clear"
    :class="{
      'media-content--windowed':
        componentSettings?.layout === 'index'
        && (isMobile || (!isMobile && contentPlacement !== 'full')),
      'media-content--full': contentPlacement === 'full',
      'media-content--half': contentPlacement === 'half',
      'media-content--default-height--desktop': desktopMediaSettings.desktopDefaultHeight,
      'media-content--default-height--mobile': mobileMediaSettings.mobileDefaultHeight,
      'media-content--reveal-effect': componentSettings.revealEffect,
      'media-content--text-section': componentSettings.contentType === 'text-section',
      'media-content--has-media': showMedia,
      'media-content--content-centered': componentSettings.titleHorizontalPosition === 'centered',
      [contentClass]: contentClass,
    }"
  >
    <div
      v-if="showMedia"
      class="media-content__media"
      :class="[
        // eslint-disable-next-line max-len
        componentSettings.verticalPosition && `media-content__media--${componentSettings.verticalPosition}`,
        // eslint-disable-next-line max-len
        componentSettings.horizontalPosition && `media-content__media--${componentSettings.horizontalPosition}`,
        componentSettings.contentColor && `media-content__media--${componentSettings.contentColor}`,
        mobileMediaSettings.mobileSwapImage && `media-content__media--hoverable`
      ]"
    >
      <art-direction
        v-if="mobileMediaSettings?.mobileImage?.src ||
          mobileMediaSettings?.mobileVideoFile ||
          desktopMediaSettings?.desktopImage?.src ||
          desktopMediaSettings?.desktopVideoFile"
        :block-class="blockClass"
        :caption="componentSettings.caption"
        caption-align="left"
        :has-hover-image="hasHoverImage"
        :image="mobileMediaSettings?.mobileImage?.src ?
          mobileMediaSettings.mobileImage :
          getPreviewImage(mobileMediaSettings.mobileVideoFile)"
        :image-desktop="desktopMediaSettings?.desktopImage?.src ?
          desktopMediaSettings?.desktopImage :
          (getPreviewImage(desktopMediaSettings.desktopVideoFile) ||
            mobileMediaSettings?.mobileImage)
        "
        :padding="false"
        :lazyload="componentSettings.lazyMedia"
        :ratio="mobileRatio"
        :ratio-desktop="desktopRatio"
        :high-priority="componentSettings.highPriorityMedia"
        @art-direction-loaded="$emit('carousel-image-loaded', true)"
      />

      <art-direction
        v-if="hasHoverImage"
        :block-class="blockClass"
        :caption="componentSettings.caption"
        :is-hover-image="hasHoverImage"
        caption-align="left"
        :image="mobileMediaSettings?.mobileSwapImage?.src ?
          mobileMediaSettings?.mobileSwapImage :
          desktopMediaSettings?.desktopSwapImage
        "
        :image-desktop="desktopMediaSettings?.desktopSwapImage?.src ?
          desktopMediaSettings?.desktopSwapImage :
          mobileMediaSettings?.mobileSwapImage
        "
        :padding="false"
        :lazyload="false"
        :ratio="mobileRatio"
        :ratio-desktop="desktopRatio"
        :high-priority="componentSettings.highPriorityMedia"
        @art-direction-loaded="$emit('carousel-image-loaded', true)"
      />

      <div
        v-if="(checkIfShopifyVideo() || checkIfVimeoVideo())"
        class="media-content__video-container"
      >
        <video
          v-if="checkIfShopifyVideo()"
          ref="browserPlayer"
          :poster="posterImage?.src"
          class="media-content__video-player"
          :autoplay="componentSettings.autoplay"
          :muted="componentSettings.muted"
          :loop="componentSettings.loop"
          playsinline
          @play="videoPlaying = true"
          @pause="videoPlaying = false"
        >
          <source :src="videoSource?.url">
        </video>

        <div
          v-if="checkIfVimeoVideo()"
          class="media-content__video-player"
        >
          <div
            ref="vimeoPoster"
            class="media-content__vimeoPoster"
          >
            <responsive-image
              v-if="posterImage"
              block-class="media-content"
              :image="posterImage"
              :padding="false"
              :ratio="false"
            />
          </div>

          <div
            ref="vimeoPlayer"
            :vimeoVideoMuted="vimeoVideoMuted"
          />
        </div>
      </div>

      <div
        v-if="componentSettings.hasControls
          && (checkIfShopifyVideo() || checkIfVimeoVideo())"
        class="media-content__controls"
        :js-sticky-child="!isMobile && contentPlacement === 'half'"
      >
        <button
          ref="statusButton"
          type="button"
          class="media-content__status-button"
        >
          <div
            ref="statusButtonPlay"
            class="media-content__status-play"
            :class="{
              [$class('hidden')]: videoPlaying,
            }"
          >
            <span
              class="
              text-desktop-regular-desktop
              text-mobile-regular-mobile
            "
              v-text="$string('accessibility.play')"
            />
          </div>
          <div
            ref="statusButtonPause"
            class="media-content__status-pause"
            :class="{
              [$class('hidden')]: !videoPlaying,
            }"
          >
            <span
              class="
              text-desktop-regular-desktop
              text-mobile-regular-mobile
            "
              v-text="$string('accessibility.pause')"
            />
          </div>
        </button>
        <div class="media-content__progress-bar-outer">
          <span
            ref="progressBarInner"
            class="media-content__progress-bar-inner"
          />
        </div>
        <button
          ref="audioButton"
          type="button"
          class="media-content__audio-button"
        >
          <span
            ref="audioButtonMute"
            class="

          text-desktop-regular-desktop
            text-mobile-regular-mobile
           "
            :class="{
              [$class('hidden')]: componentSettings.muted,
            }"
            v-text="$string('accessibility.mute')"
          />

          <span
            ref="audioButtonUnMute"
            class="

          text-desktop-regular-desktop
            text-mobile-regular-mobile
           "
            :class="{
              [$class('hidden')]: !componentSettings.muted,
            }"
            v-text="$string('accessibility.unmute')"
          />
        </button>
      </div>
    </div>

    <div
      v-if="tabSettings"
      class="media-content__content media-content__content--tabs"
    >
      <ul class="media-content__tabs-list">
        <template
          v-for="(tab, index) of tabSettings.links"
          :key="`media-content__tab-${index}`"
        >
          <li
            v-if="tab.url"
          >
            <a

              class="media-content__tab text-desktop-14-desktop text-mobile-14-mobile"
              :class="{
                'media-content__tab--active': tab.active,
              }"
              :href="tab.url"
              v-text="tab.title"
            />
          </li>
        </template>
      </ul>

      <div
        v-if="tabSettings.text"
        class="media-content__tabs-text text-desktop-14-desktop text-mobile-14-mobile"
        v-html="tabSettings.text"
      />
    </div>

    <div
      v-else-if="showContent &&
        (
          componentSettings.kickerLine1
          ||
          componentSettings.kickerLine2
          ||
          componentSettings.titleText
          ||
          componentSettings.buttonText)
        && !linkSettings[0]?.title"
      class="media-content__content"
      :class="{
        'media-content__content--sticky': componentSettings?.hasStickyContent,
        [`media-content__content--one-row`]:
          componentSettings?.titleAndButtonOneRowLayout,
        [`media-content__content--${componentSettings.titleHorizontalPosition}`]:
          componentSettings?.titleHorizontalPosition,
        [`media-content__content--${componentSettings.titleVerticalPosition}`]:
          componentSettings?.titleVerticalPosition,
      }"
    >
      <div
        v-if="
          // @TODO: Make a component of this instead of repeating
          // (display: flex and order in CSS caused other issues)
          (componentSettings.kickerLine1 || componentSettings.kickerLine2)
            && componentSettings.kickerPosition === 'above-title'"
        class="media-content__kicker"
        :class="{
          [`media-content__kicker--${componentSettings.kickerPosition}`]:
            componentSettings?.kickerPosition,
        }"
      >
        <span
          class="text-desktop-small text-mobile-small"
          v-html="componentSettings.kickerLine1"
        />
        <span
          v-if="componentSettings.kickerLine2"
          class="text-desktop-regular-uc text-mobile-regular-uc"
          v-html="componentSettings.kickerLine2"
        />
      </div>
      <template v-if="!linkSettings[0]?.title">
        <div
          v-if="componentSettings.titleText"
          class="media-content__title"
          :class="{
            'media-content__title--mb': componentSettings?.titleMarginBottom,
            [componentSettings.titleMobileSize]:
              componentSettings?.titleMobileSize,
            [componentSettings.titleDesktopSize]:
              componentSettings?.titleDesktopSize
          }"
          v-html="componentSettings.titleText"
        />

        <template v-if="componentSettings.titleAndButtonOneRowLayout">
          <icon-chevron-right />
        </template>

        <a
          v-if="componentSettings.buttonText && componentSettings.buttonURL"
          class="media-content__button text-regular"
          :href="componentSettings.buttonURL"
          aria-role="button"
          v-html="componentSettings.buttonText"
        />
      </template>

      <div
        v-if="(componentSettings.kickerLine1 || componentSettings.kickerLine2)
          && componentSettings.kickerPosition === 'below-title'"
        class="media-content__kicker"
        :class="{
          [`media-content__kicker--${componentSettings.kickerPosition}`]:
            componentSettings?.kickerPosition,
        }"
      >
        <span
          class="text-desktop-small text-mobile-small"
          v-html="componentSettings.kickerLine1"
        />
        <span
          v-if="componentSettings.kickerLine2"
          class="text-desktop-regular-uc text-mobile-regular-uc"
          v-html="componentSettings.kickerLine2"
        />
      </div>

      <div
        v-if="componentSettings.bodyText"
        class="media-content__body-text text-desktop-14-desktop
          text-mobile-14-mobile"
        v-html="componentSettings.bodyText"
      />
    </div>

    <ul
      v-if="linkSettings[0]?.title"
      class="media-content__links"
      :class="{
        'media-content__links--sticky': componentSettings?.hasStickyContent,
        [`media-content__links--${componentSettings.linksPosition}`]:
          componentSettings?.linksPosition}"
    >
      <template
        v-for="(link, index) of linkSettings"
        :key="`media-content__link-${index}`"
      >
        <li
          v-if="link.url"
        >
          <a

            class="media-content__link text-desktop-regular text-mobile-regular"
            :href="link.url"
          >
            <span v-text="link.title" /> <span
              class="text-bold"
              v-text="link.text"
            />
          </a>
        </li>
      </template>
    </ul>
    <a
      v-if="hasLink"
      :href="sectionUrl"
      class="fauxlink"
    />
  </div>
</template>

<script>

/**
 * Vue: Background media (media-content)
 * -----------------------------------------------------------------------------
 * Snippet for background media.
 *
 * @param {String} contentPlacement - String indicating if the component is
 * occupying half or full width.
 * @param {String} parent - Name of the parent component.
 */

import Player from '@vimeo/player'

import { mapState } from 'vuex'
import ResponsiveImage from '~global/images/responsive-image'
import ArtDirection from '~global/images/art-direction'
import IconChevronRight from '~icons/direction/chevron/right.svg'
export default {
  name: 'MediaContent',

  components: {
    ArtDirection,
    ResponsiveImage,
    IconChevronRight,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },

    contentClass: {
      type: String,
      default: '',
    },

    contentPlacement: {
      type: String,
      default: 'full',
    },

    showMedia: {
      type: Boolean,
      default: true,
    },

    showContent: {
      type: Boolean,
      default: true,
    },

    componentSettings: {
      type: Object,
      default: () => ({
        titleText: '',
        titleMobileSize: '',
        titleDesktopSize: '',
        titleHorizontalPosition: '',
        titleVerticalPosition: '',
        titleAndButtonOneRowLayout: false,
        bodyText: '',
        bodyMobileSize: '',
        bodyDesktopSize: '',
        buttonText: '',
        buttonURL: '',
        kickerLine1: '',
        kickerLine2: '',
        kickerPosition: '',
        linksPosition: '',
        caption: '',
        layout: '',
        hasControls: false,
        hasContent: false,
        autoplay: true,
        loop: true,
        muted: true,
        paddingTopMobile: '',
        paddingBottomMobile: '',
        paddingBottomDesktop: '',
        paddingTopDesktop: '',
        horizontalPosition: '',
        verticalPosition: '',
        revealEffect: false,
        lazyMedia: true,
        highPriorityMedia: false,
      }),
    },

    linkSettings: {
      type: Array,
      default: () => [
        {
          title: '',
          text: '',
          url: '',
        },
      ],
    },

    tabSettings: {
      type: [Boolean, Object],
      default: false,
    },

    mobileMediaSettings: {
      type: Object,
      default: () => ({
        mobileDefaultHeight: false,
        mobileVideoFile: '',
        mobileVimeoId: '',
        mobileImage: '',
        mobileSwapImage: '',
      }),
    },

    desktopMediaSettings: {
      type: Object,
      default: () => ({
        desktopDefaultHeight: false,
        desktopVideoFile: '',
        desktopVimeoId: '',
        desktopImage: '',
        desktopSwapImage: '',
      }),
    },

    parent: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      findParent: false,
      player: null,
      posterImage: null,
      videoPlaying: false,
      vimeoVideoMuted: this.componentSettings.muted,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    hasHoverImage() {
      return this.mobileMediaSettings?.mobileSwapImage?.src ||
        this.desktopMediaSettings?.desktopSwapImage?.src
    },

    /**
     * Computes media caption
     */
    caption() {
      if (this.componentSettings?.layout === 'index' || this.parent === 'page-media') {
        return this.componentSettings.caption
      }

      return ''
    },

    /**
     * Computes and returns a mobile image aspect ratio based on image size.
     */
    mobileAspectRatio() {
      const image = this.mobileMediaSettings.mobileImage

      if (!image.aspectRatio && (!image.width || !image.height)) {
        return 'unset'
      }

      if (image.aspectRatio || image.aspect_ratio) {
        // return Number((image.width / image.height).toFixed(2)) + 0.01
        return Number(image.aspectRatio || image.aspect_ratio)

      }

      // return Number((image.width / image.height).toFixed(2)) + 0.01
      return Number(image.width / image.height) + 0.01
    },

    /**
     * Computes and returns a mobile image aspect ratio based on section setup.
     */
    mobileRatio() {
      if (this.mobileMediaSettings.mobileDefaultHeight || this.parent === 'page-media') {
        return 'image'
      }

      return 0.72
    },

    /**
     * Computes and returns a desktop image aspect ratio based on image size.
     */
    desktopAspectRatio() {
      const image = this.desktopMediaSettings.desktopImage

      if (!image.aspectRatio && (!image.width || !image.height)) {
        return 'unset'
      }

      if (image.aspectRatio || image.aspect_ratio) {
        return image.aspectRatio || image.aspect_ratio
      }

      return image.width / image.height
    },

    /**
     * Computes and returns a desktop image aspect ratio based on section setup.
     */
    desktopRatio() {
      if (this.desktopMediaSettings.desktopDefaultHeight || this.parent === 'page-media') {
        return 'image'
      }

      return this.contentPlacement === 'half' ? 0.72 : 1.6
    },

    /**
     * Returns the best video src from the supplied video (based on breakpoint).
     */
    videoSource() {
      return this.isMobile
        ? this.getMp4Source(this.mobileMediaSettings.mobileVideoFile)?.[0]
        : this.getMp4Source(this.desktopMediaSettings.desktopVideoFile)?.[0]
    },

    /**
     * Compute if the button is a link.
     * @returns {String}
     */
    hasLink() {
      return this.componentSettings.buttonURL
    },

    /**
     * Compute section url.
     * @returns {String}
     */
    sectionUrl() {
      return this.componentSettings.buttonURL ? this.componentSettings.buttonURL : null
    },
  },

  watch: {
    videoPlaying() {
      if (this.checkIfShopifyVideo()) {
        this.$refs.browserPlayer.addEventListener('ended', () => {
          this.toggleStatusButton()
        })
      }
    },
  },

  mounted() {
    this.getPoster()
    this.handleScroll()

    if (this.checkIfVimeoVideo()) {
      this.initVimeoPlayer()
    }

    if (this.componentSettings.hasControls &&
      (this.$refs.browserPlayer || this.$refs.vimeoPlayer)) {
      this.setEventListeners()
    }

    this.$nextTick().then(() => {
      this.findParent = true
    })
  },

  methods: {

    /**
     * Returns the highest resolution mp4 video source from the supplied video.
     * @param {Object} video - Shopify video object.
     * @returns {Array}
     */
    getMp4Source(video) {
      const sources = [...video.sources.filter(source => source.format === 'mp4')]
      return sources.sort((a, b) => b.width - a.width)
    },

    /**
     * Returns the preview image from the 2 video formats.
     * @param {Object} video - Video object of 2 possible formats.
     * @returns {Object}
     */
    getPreviewImage(video) {
      return video?.preview_image?.src
        ? video.preview_image
        : false
    },

    /**
     * Initalize Vimeo Player
     */
    initVimeoPlayer() {
      const options = {
        autoplay: this.componentSettings.autoplay,
        loop: this.componentSettings.loop,
        muted: this.componentSettings.muted,
        controls: false,
        title: false,
        transparent: false,
        portrait: false,
        byline: false,
        dnt: true,
        id: this.isMobile
          ? this.mobileMediaSettings.mobileVimeoId
          : this.desktopMediaSettings.desktopVimeoId,
      }
      this.vimeoPlayer = new Player(this.$refs.vimeoPlayer, options)
    },

    /**
     * Gets the node to teleport the element to.
     * This is required only if the element is nested inside ANY parent that
     * has transform set as a CSS property, which prevents the fixed positioning
     * taking effect.
     */
    getTeleportNode() {
      const parent = this.$el?.closest('[js-sticky-parent]')
      return parent
    },

    /**
     * Set Event Listeners
     */
    setEventListeners() {

      if (this.checkIfVimeoVideo()) {
        this.$refs.statusButton.addEventListener('click', () => this.videoControls('status', 'vimeo'))
        this.$refs.audioButton.addEventListener('click', () => this.videoControls('audio', 'vimeo'))

        this.vimeoPlayer.on('timeupdate', () => this.videoProgress('vimeo'))

        if (this.componentSettings.autoplay) {
          this.videoPlaying = true
          this.vimeoPlayer.on('loaded', this.removePoster)
        }

        this.vimeoPlayer.on('ended', () => {
          this.videoPlaying = false
          this.toggleStatusButton()
        })

      } else if (this.checkIfShopifyVideo()) {
        this.$refs.statusButton.addEventListener('click', () => this.videoControls('status', 'browser'))
        this.$refs.audioButton.addEventListener('click', () => this.videoControls('audio', 'browser'))

        this.$refs.browserPlayer.addEventListener('timeupdate', () => this.videoProgress('browser'))
      }
    },

    /**
     * Control video events based on type of video player
     */
    videoControls(event, playerType) {
      if (playerType === 'browser') {
        if (event === 'status') {
          if (this.$refs.browserPlayer.paused === false &&
            this.$refs.browserPlayer.ended === false) {
            this.$refs.browserPlayer.pause()
          } else {
            this.$refs.browserPlayer.play()
          }
        } else if (event === 'audio') {
          if (this.$refs.browserPlayer.muted) {
            this.$refs.browserPlayer.muted = false
          } else {
            this.$refs.browserPlayer.muted = true
          }
          this.toggleAudioButton()
        }
      } else if (playerType === 'vimeo') {
        if (event === 'status') {
          this.vimeoPlayer.getPaused().then((paused) => {
            if (paused) {
              this.videoPlaying = true
              this.vimeoPlayer.play()

              if (!this.componentSettings.autoplay) {
                this.removePoster()
              }
            } else {
              this.videoPlaying = false
              this.vimeoPlayer.pause()
            }
          })
        } else if (event === 'audio') {
          this.toggleAudioButton()

          if (this.vimeoVideoMuted) {
            this.vimeoPlayer.setMuted(true)

          } else {
            this.vimeoPlayer.setMuted(false)
          }
        }
      }
    },

    /**
     * Update video progress bar based on current time
     */
    videoProgress(playerType) {
      if (playerType === 'browser') {
        const currentTime = this.$refs.browserPlayer.currentTime
        const duration = this.$refs.browserPlayer.duration

        const progress = (100 * currentTime) / duration

        this.$refs.progressBarInner.style.width = `${progress}%`
      } else if (playerType === 'vimeo') {
        this.vimeoPlayer.getCurrentTime().then((currentTime) => {
          this.vimeoPlayer.getDuration().then((duration) => {
            const progress = (100 * currentTime) / duration
            this.$refs.progressBarInner.style.width = `${progress}%`
          })
        })
      }
    },

    /**
     * Get poster image source
     */
    getPoster() {
      this.posterImage = this.isMobile
        ? this.mobileMediaSettings?.mobileImage
        : this.desktopMediaSettings?.desktopImage
    },

    /**
     * Remove poster in vimeo
     */
    removePoster() {
      this.$refs.vimeoPoster.classList.add(this.$class('hidden'))
    },

    /**
     * Toggle Player status buttons
     */
    toggleStatusButton() {
      this.$refs.statusButtonPause.classList.toggle(this.$class('hidden'))
      this.$refs.statusButtonPlay.classList.toggle(this.$class('hidden'))
    },

    /**
     * Toggle Player mute/unmute buttons
     */
    toggleAudioButton() {
      this.vimeoVideoMuted = !this.vimeoVideoMuted
      this.$refs.audioButtonMute.classList.toggle(this.$class('hidden'))
      this.$refs.audioButtonUnMute.classList.toggle(this.$class('hidden'))

    },

    /**
     * Check if shopify video is used
     */
    checkIfShopifyVideo() {
      return (this.mobileMediaSettings.mobileVideoFile && this.isMobile) ||
        (this.desktopMediaSettings.desktopVideoFile && !this.isMobile)
    },

    /**
     * Check if vimeo video is used
     */
    checkIfVimeoVideo() {
      return ((this.mobileMediaSettings.mobileVimeoId && this.isMobile) ||
        (this.desktopMediaSettings.desktopVimeoId && !this.isMobile)) &&
        ((!this.mobileMediaSettings.mobileVideoFile && this.isMobile) ||
          (!this.desktopMediaSettings.desktopVideoFile && !this.isMobile))
    },

    /**
     * Detect if Preorder CTA section is at the end of page.
     * Change BG to black if at bottom
     */
    handleScroll() {
      if (!this.componentSettings.revealEffect) {
        return
      }

      window.addEventListener('scroll', () => {
        const container = this.$refs.container

        if (
          container.getBoundingClientRect().top <= 0
        ) {
          container.classList.remove('media-content--fixed')
        } else {
          container.classList.add('media-content--fixed')
        }

      })
    },
  },
}
</script>

<style lang="scss">
@import './media-content';

.fauxlink {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1;
  height: 90%;
}
</style>
